
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop, Ref } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { TeamEffectivenessV2ReportData } from "@/store/admin/adminTypes";
import { TRAIT_CLASS_RANGES, TRAITS, Trait } from "@/lib/consts";
import CompetencyMatrix, { MatrixView } from "@/components/admin/CompetencyMatrix.vue";
import FilterBar from "./FilterBar.vue";
import TeamCompetencySpread from "./TeamCompetencySpread.vue";
import { CompetencyStat, displayName, LeaderSummary, ReportFilters, Subgroup } from "./shared";
import CandidatePicker, { CandidatePickerOption } from "@/components/project-reports/CandidatePicker.vue";

@Component({ components: { CandidatePicker, CompetencyMatrix, FilterBar, TeamCompetencySpread } })
export default class ByLeaderTab extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true }) readonly reportData!: TeamEffectivenessV2ReportData;
  @Prop({ required: true }) readonly leaders!: LeaderSummary[];
  @Prop({ required: true }) readonly visibleLeaders!: LeaderSummary[];
  @Prop({ required: true }) readonly subgroups!: Subgroup[];
  @Prop({ required: true }) readonly filters!: ReportFilters;
  @Prop({ required: true }) readonly stats!: CompetencyStat[];
  @Prop({ default: false }) readonly printing!: boolean;

  public matrixView: MatrixView = "top-priority";
  public showChampions = false;
  public matrixKey = 0;
  public allowedCompetencies: Trait[] = [...TRAITS];

  // The shared matrix draws its header shades as fixed 225×65px strips rotated 60°, which tile
  // exactly for its 75px columns (65 / sin 60° = 75). Inside this card the columns are narrower,
  // so each strip would spill onto the column to its right. We measure the real column width
  // (on mount, re-render and window resize) and trim the strips to it via a CSS variable (see
  // the ::v-deep rule below).
  @Ref("matrixScroll") readonly matrixScroll!: HTMLDivElement;
  public matrixColWidth = 75;

  public matrixViews: Array<{ key: MatrixView; label: string }> = [
    { key: "top-priority", label: "ByLeader::Matrix::Tab::GroupPriorities" },
    { key: "must-have", label: "ByLeader::Matrix::Tab::Critical" },
    { key: "custom", label: "ByLeader::Matrix::Tab::Custom" },
  ];

  get traitClassRanges() {
    return TRAIT_CLASS_RANGES;
  }

  get matrixCacheKey(): string {
    return `te2-matrix-${this.$route.params.id}`;
  }

  // CompetencyMatrix sorts its input in place, so hand it a copy.
  get matrixAssessments() {
    return this.visibleLeaders.map((l) => l.assessment);
  }

  get visibleLeaderIds(): string[] {
    return this.visibleLeaders.map((l) => l.id);
  }

  // With names hidden, initials would give the leader away, so derive them from the anonymised label too.
  get pickerOptions(): CandidatePickerOption[] {
    const hide = this.filters.hideNames;
    return this.leaders.map((l) => ({ id: l.id, name: displayName(l, hide), initials: hide ? l.label.substring(0, 2) : l.initials, color: l.color }));
  }

  toggleLeader(id: string): void {
    const ids = this.filters.activeLeaderIds.includes(id) ? this.filters.activeLeaderIds.filter((x) => x !== id) : [...this.filters.activeLeaderIds, id];
    this.$emit("update-filters", { activeLeaderIds: ids });
  }

  resetMatrixOrder(): void {
    window.localStorage.removeItem(this.matrixCacheKey);
    this.matrixKey++;
  }

  measureMatrixColumn(): void {
    const th = this.matrixScroll?.querySelector("th.trait-header") as HTMLElement | null;
    const width = th?.getBoundingClientRect().width ?? 0;
    if (width > 0) this.matrixColWidth = width;
  }

  mounted(): void {
    // The matrix only renders its header cells after its own mount hook, which re-renders it on
    // the next tick without triggering our `updated`, so measure again once that has happened.
    this.measureMatrixColumn();
    this.$nextTick(this.measureMatrixColumn);
    window.addEventListener("resize", this.measureMatrixColumn);
  }

  updated(): void {
    this.measureMatrixColumn();
  }

  beforeDestroy(): void {
    window.removeEventListener("resize", this.measureMatrixColumn);
  }
}
